
import React from 'react';
import { X, Plus, ThumbsUp, ThumbsDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Poll, PollOption } from '@/types/poll';
import { Slider } from '@/components/ui/slider';

// Update the interface to use currentPoll and setCurrentPoll
interface PollOptionsEditorProps {
  currentPoll: Poll;
  setCurrentPoll: React.Dispatch<React.SetStateAction<Poll | null>>;
}

const PollOptionsEditor: React.FC<PollOptionsEditorProps> = ({ 
  currentPoll, 
  setCurrentPoll 
}) => {
  // Get the options directly from the currentPoll
  const options = currentPoll.options;

  // Helper function to update the options
  const setOptions = (newOptions: PollOption[]) => {
    setCurrentPoll({
      ...currentPoll,
      options: newOptions,
      questions: {
        ...currentPoll.questions,
        options: newOptions
      }
    });
  };
  
  // Function to add a new option
  const handleAddOption = () => {
    // Find the highest ID and increment by 1
    const maxId = options.reduce((max, option) => Math.max(max, option.id), 0);
    
    // Default sentiment based on position (first two options have standard values, others neutral)
    let defaultSentiment = 0;
    if (options.length === 0) {
      defaultSentiment = 100; // First option (agree)
    } else if (options.length === 1) {
      defaultSentiment = -100; // Second option (disagree)
    }
    
    const newOption: PollOption = { 
      id: maxId + 1, 
      text: '', 
      votes: 0,
      sentiment: defaultSentiment
    };
    
    setOptions([...options, newOption]);
  };
  
  // Function to remove an option
  const handleRemoveOption = (idToRemove: number) => {
    // Don't allow removing if only 2 options left
    if (options.length <= 2) {
      return;
    }
    
    const newOptions = options.filter(option => option.id !== idToRemove);
    setOptions(newOptions);
  };
  
  // Function to update option text
  const handleOptionTextChange = (id: number, newText: string) => {
    const newOptions = options.map(option => 
      option.id === id ? { ...option, text: newText } : option
    );
    setOptions(newOptions);
  };
  
  // Function to update option sentiment value
  const handleOptionSentimentChange = (id: number, newSentiment: number) => {
    const newOptions = options.map(option => 
      option.id === id ? { ...option, sentiment: newSentiment } : option
    );
    setOptions(newOptions);
  };
  
  // Function to get sentiment label
  const getSentimentLabel = (value: number | undefined): string => {
    if (value === undefined) return 'Neutral';
    if (value >= 75) return 'Strong Agreement';
    if (value >= 25) return 'Agreement';
    if (value > -25) return 'Neutral';
    if (value > -75) return 'Disagreement';
    return 'Strong Disagreement';
  };
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-medium">Poll Options</h3>
        <Button 
          type="button"
          onClick={handleAddOption}
          variant="outline"
          size="sm"
          className="flex items-center gap-1"
        >
          <Plus className="h-4 w-4" />
          Add Option
        </Button>
      </div>
      
      <div className="space-y-4">
        {options.map((option, index) => (
          <div key={option.id} className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="flex-grow">
                <Input
                  placeholder={`Option ${index + 1}`}
                  value={option.text}
                  onChange={(e) => handleOptionTextChange(option.id, e.target.value)}
                  className="w-full"
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleRemoveOption(option.id)}
                disabled={options.length <= 2}
                className="text-red-500 hover:text-red-700 hover:bg-red-50"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Sentiment slider section */}
            <div className="pl-1 pr-1">
              <div className="flex justify-between items-center text-sm text-muted-foreground mb-1">
                <div className="flex items-center">
                  <ThumbsDown className="h-3 w-3 mr-1 text-red-500" />
                  <span>Disagree</span>
                </div>
                <div className="font-medium">
                  {getSentimentLabel(option.sentiment)}
                </div>
                <div className="flex items-center">
                  <span>Agree</span>
                  <ThumbsUp className="h-3 w-3 ml-1 text-green-500" />
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="flex-grow">
                  <Slider
                    min={-100}
                    max={100}
                    step={5}
                    value={[option.sentiment ?? 0]}
                    onValueChange={(values) => handleOptionSentimentChange(option.id, values[0])}
                    className="my-1"
                  />
                </div>
                <div className="w-10 text-center font-mono text-sm">
                  {option.sentiment ?? 0}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {options.length <= 2 && (
        <p className="text-sm text-muted-foreground">
          Polls require at least two options.
        </p>
      )}
    </div>
  );
};

export default PollOptionsEditor;
